import { PropsWithChildren, useEffect, useReducer, useState } from 'react'
import {
  AuthContext,
  authStatePersistedKeys,
  initialAuthState,
} from '../contexts/AuthState.context'
import {
  AuthActionType,
  authReducer,
  AuthState,
} from '../reducers/AuthState.reducer'
import { LOCAL_STORAGE_KEY } from '../utils/config'
import { pick } from '../utils/pick'
import { fetcher } from '../utils/fetcher'
import { UserBM } from '@bm-js/atlascloud-shared'

export const AuthContextProvider = ({ children }: PropsWithChildren) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [state, dispatch] = useReducer(authReducer, initialAuthState)

  useEffect(() => {
    const storage = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (isInitialLoad) {
      const parsedStorage = JSON.parse(storage || '{}') || ({} as AuthState)
      const pickedState = pick(parsedStorage, ...authStatePersistedKeys)
      dispatch({ type: AuthActionType.mergeState, data: pickedState })
      setIsInitialLoad(false)
      return
    }

    const stateToPersist = pick(state, ...authStatePersistedKeys)
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(stateToPersist))
  }, [state, isInitialLoad])

  const handleUnauthenticated = () => {
    dispatch({
      type: AuthActionType.replaceState,
      data: { isAuthenticating: false },
    })
  }

  const handleTokenChange = async (token?: string) => {
    if (!token) return handleUnauthenticated()
    const { data: user, err } = await fetcher<UserBM>({
      path: 'customer/verify-token',
      auth: token,
    })

    if (err) return handleUnauthenticated()
    window.atlasCloudCustomerId = user?.customerId as string
    window.atlasCloudIsEditorContext = true
    dispatch({
      type: AuthActionType.mergeState,
      data: { user, isAuthenticating: false },
    })
  }

  useEffect(() => {
    if (isInitialLoad) return
    handleTokenChange(state.token)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.token, isInitialLoad])

  return (
    <AuthContext.Provider value={{ state, dispatch }}>
      {children}
    </AuthContext.Provider>
  )
}
